import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import useEscapeKey from "../hooks/useEscapeKey";
import {
  createScenario,
  fetchScenarioDefaults,
  updateAndRunScenario,
  type ScenarioResponse,
} from "../api/client";
import { useToastContext } from "../contexts/ToastContext";

// ---------------------------------------------------------------------------
// Shared types & helpers
// ---------------------------------------------------------------------------

interface AssumptionFields {
  exit_cap_rate: string;
  discount_rate: string;
  expense_growth_rate_pct: string;
  vacancy_rate_pct: string;
  market_rent_growth_pct_annual: string;
}

const LABELS: { key: keyof AssumptionFields; label: string }[] = [
  { key: "exit_cap_rate", label: "Exit Cap Rate (%)" },
  { key: "discount_rate", label: "Discount Rate (%)" },
  { key: "expense_growth_rate_pct", label: "Expense Growth (%)" },
  { key: "vacancy_rate_pct", label: "Vacancy Rate (%)" },
  { key: "market_rent_growth_pct_annual", label: "Market Rent Growth (%)" },
];

const emptyFields = (): AssumptionFields => ({
  exit_cap_rate: "",
  discount_rate: "",
  expense_growth_rate_pct: "",
  vacancy_rate_pct: "",
  market_rent_growth_pct_annual: "",
});

function toDecimal(
  fields: AssumptionFields
): Record<string, number> | undefined {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (v.trim()) out[k] = parseFloat(v) / 100;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function fromDecimal(d: Record<string, number>): AssumptionFields {
  const f = (key: string) => (d[key] ? (d[key] * 100).toFixed(1) : "");
  return {
    exit_cap_rate: f("exit_cap_rate"),
    discount_rate: f("discount_rate"),
    expense_growth_rate_pct: f("expense_growth_rate_pct"),
    vacancy_rate_pct: f("vacancy_rate_pct"),
    market_rent_growth_pct_annual: f("market_rent_growth_pct_annual"),
  };
}

function fromScenario(
  a: Record<string, unknown> | null
): AssumptionFields {
  if (!a) return emptyFields();
  const f = (key: string) => {
    const v = a[key];
    return typeof v === "number" ? (v * 100).toFixed(1) : "";
  };
  return {
    exit_cap_rate: f("exit_cap_rate"),
    discount_rate: f("discount_rate"),
    expense_growth_rate_pct: f("expense_growth_rate_pct"),
    vacancy_rate_pct: f("vacancy_rate_pct"),
    market_rent_growth_pct_annual: f("market_rent_growth_pct_annual"),
  };
}

// ---------------------------------------------------------------------------
// Shared UI
// ---------------------------------------------------------------------------

function AssumptionGrid({
  fields,
  setFields,
}: {
  fields: AssumptionFields;
  setFields: React.Dispatch<React.SetStateAction<AssumptionFields>>;
}) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {LABELS.map(({ key, label }) => (
        <div key={key}>
          <label className="block text-xs text-bark-400 mb-1.5">{label}</label>
          <input
            type="number"
            step="0.1"
            value={fields[key]}
            onChange={(e) =>
              setFields((prev) => ({ ...prev, [key]: e.target.value }))
            }
            placeholder="--"
            className="w-full px-3 py-2.5 text-sm bg-white border border-grove-300 rounded-lg text-bark-700 placeholder:text-bark-300"
          />
        </div>
      ))}
    </div>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEscapeKey(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bark-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl border border-grove-300 shadow-2xl w-full max-w-lg mx-4 p-8 animate-scale-in">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-xl font-semibold text-bark-800">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-grove-100 transition-colors"
          >
            <X className="w-5 h-5 text-bark-400" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({
  onClose,
  submitting,
  label,
  submittingLabel,
}: {
  onClose: () => void;
  submitting: boolean;
  label: string;
  submittingLabel: string;
}) {
  return (
    <div className="flex gap-3 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="flex-1 px-4 py-3 text-sm font-medium border border-grove-300 rounded-lg text-bark-500 hover:bg-grove-50 transition-all"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={submitting}
        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold bg-honey-600 text-white rounded-lg hover:bg-honey-500 disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {submitting ? submittingLabel : label}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Modal
// ---------------------------------------------------------------------------

export function CreateScenarioModal({
  dealId,
  onClose,
  onCreated,
}: {
  dealId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [fields, setFields] = useState(emptyFields());
  const [submitting, setSubmitting] = useState(false);
  const { addToast } = useToastContext();

  useEffect(() => {
    fetchScenarioDefaults(dealId)
      .then((res) => setFields(fromDecimal(res.assumptions)))
      .catch(() => {});
  }, [dealId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await createScenario(dealId, {
        name: name.trim(),
        assumptions: toDecimal(fields),
      });
      onCreated();
    } catch {
      addToast("Failed to create scenario.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="New Scenario" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-bark-600 mb-2">
            Scenario Name
          </label>
          <input
            autoFocus
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Bear Case"
            className="w-full px-4 py-3 text-sm bg-white border border-grove-300 rounded-lg text-bark-700 placeholder:text-bark-300"
          />
        </div>
        <div>
          <h3 className="text-sm font-medium text-bark-600 mb-3">
            Assumptions (% - leave blank for base case defaults)
          </h3>
          <AssumptionGrid fields={fields} setFields={setFields} />
        </div>
        <ModalActions
          onClose={onClose}
          submitting={submitting}
          label="Create & Run"
          submittingLabel="Creating..."
        />
      </form>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Edit Modal
// ---------------------------------------------------------------------------

export function EditScenarioModal({
  dealId,
  scenario,
  onClose,
  onSaved,
}: {
  dealId: string;
  scenario: ScenarioResponse;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(scenario.name);
  const [fields, setFields] = useState(fromScenario(scenario.assumptions));
  const [submitting, setSubmitting] = useState(false);
  const { addToast } = useToastContext();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await updateAndRunScenario(dealId, scenario.id, {
        name: name.trim(),
        assumptions: toDecimal(fields),
      });
      onSaved();
    } catch {
      addToast("Failed to update scenario.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title={`Edit: ${scenario.name}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-bark-600 mb-2">
            Scenario Name
          </label>
          <input
            autoFocus
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-3 text-sm bg-white border border-grove-300 rounded-lg text-bark-700 placeholder:text-bark-300"
          />
        </div>
        <div>
          <h3 className="text-sm font-medium text-bark-600 mb-3">
            Assumptions (%)
          </h3>
          <AssumptionGrid fields={fields} setFields={setFields} />
        </div>
        <ModalActions
          onClose={onClose}
          submitting={submitting}
          label="Save & Run"
          submittingLabel="Running..."
        />
      </form>
    </ModalShell>
  );
}
